import { currentTabCompanySession, installTabCompanySession } from "../contexts/tabCompanySession";
import { closeTenantScopeForDocument } from "../contexts/tenantScopeHeaders";
/**
 * Shared logout utility to clear all client-side caches and state.
 *
 * This should be called whenever the user logs out or when auth tokens expire/become invalid.
 * Clears:
 * - File cache (OPFS - Origin Private File System)
 * - Query cache (React Query - memory + IndexedDB)
 * - All localStorage tokens and tenant-specific data
 */

import { clearFileCache } from "../components/fileviewer/loadFileAndCache";
import { invalidateQueryCacheEpoch } from "../contexts/queryCacheEpoch";
import { clearAllPersistedCaches } from "../contexts/TenantQueryClientProvider";
import { getPlatform } from "../platform/platformRef";
import { clearAdminOpsJournal } from "../store/adminOpsJournal";
import { PrimeDebugLog } from "../util/debugLog";
import { runSignOutCleanups } from "./signOutCleanup";
import { listenForSignOut } from "./signOutBroadcast";
import { PERSONAL_DATABASES } from "./personalStorage";
import { signOutClearsLocal, signOutClearsSession } from "./signOutStorage";

export { AUTH_REDIRECT_KEY } from "./authReturn";

/** Deletes a database whose entries are all a person's (`PERSONAL_DATABASES`). One this or
 *  another tab holds open stays queued for deletion until it closes; sign-out does not wait. */
function deleteDatabase(name: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") return resolve();
        const request = indexedDB.deleteDatabase(name);
        request.onsuccess = () => resolve();
        request.onblocked = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/** This tab's own sign-out keys. Blocked session storage must not prevent durable local cleanup. */
function clearTabSessionStorage(): void {
    try {
        const allKeys = Array.from({ length: sessionStorage.length }, (_, i) =>
            sessionStorage.key(i),
        );
        for (const key of allKeys)
            if (key && signOutClearsSession(key)) sessionStorage.removeItem(key);
    } catch (error) {
        console.warn("[Logout] Failed to clear session storage:", error);
    }
}

/** Deletes every local and session storage key `signOutStorage.ts` says sign-out deletes. */
function clearAllLocalStorageCache(): void {
    clearTabSessionStorage();
    const allKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    for (const key of allKeys) if (key && signOutClearsLocal(key)) localStorage.removeItem(key);
}

/**
 * Clear the persisted query caches of every tenant.
 *
 * The synchronous epoch invalidation below makes pre-logout snapshots
 * unrestorable, so a browser-stalled IndexedDB clear is bounded rather than
 * blocking logout indefinitely.
 */
async function clearQueryCacheForLogout(durableInvalidation: boolean): Promise<void> {
    const clearing = clearAllPersistedCaches();
    if (!durableInvalidation) return clearing;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            clearing,
            new Promise<never>((_, reject) => {
                timer = setTimeout(
                    () =>
                        reject(
                            new Error(
                                "Query cache cleanup is still pending; old snapshots are invalidated",
                            ),
                        ),
                    5000,
                );
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/** A project-URL Document's credential is dropped and its scope closed; true when it held one. */
function retireProjectCredential(): boolean {
    if (!currentTabCompanySession()) return false;
    closeTenantScopeForDocument();
    installTabCompanySession(null);
    return true;
}

/**
 * Clear all caches. Use this on logout to ensure complete cleanup.
 * This clears all tenant data, not just the current tenant.
 */
export async function performLogout(): Promise<void> {
    // Retire the canonical document before async cleanup, then synchronously
    // invalidate any persisted cache a subsequent user could otherwise read.
    retireProjectCredential();
    const invalidated = invalidateQueryCacheEpoch();
    // Before localStorage goes: a persisted store's cleared state is written, then removed.
    runSignOutCleanups();
    try {
        clearAllLocalStorageCache();
    } catch (error) {
        console.warn("[Logout] Failed to clear localStorage cache:", error);
    }
    const results = await Promise.allSettled([
        clearFileCache(),
        clearQueryCacheForLogout(invalidated),
        getPlatform().prefs.clear(),
        ...PERSONAL_DATABASES.map(deleteDatabase),
    ]);
    const labels = ["file cache", "query cache", "platform prefs", ...PERSONAL_DATABASES];
    results.forEach((result, i) => {
        if (result.status === "rejected") {
            console.warn(`[Logout] Failed to clear ${labels[i]}:`, result.reason);
        }
    });
    clearAdminOpsJournal();
    PrimeDebugLog("Logout", "Cache cleanup attempted; prior query snapshots invalidated");
}

/**
 * Another tab of this browser signed out (`announceSignOut`), so this one ends its own part: a
 * project-URL tab drops its credential and closes its scope, and every tab forgets the person in
 * memory and in its own sessionStorage (a switch's credential handoff, the person's drafts). The
 * signing-out tab has already deleted what the tabs share. A project-URL tab then reloads and
 * meets sign-in as any signed-out visit does; a legacy tab meets it at its next request, which
 * the revoked cookie session fails (`config/axios`).
 */
function signedOutElsewhere(): void {
    const projectTab = retireProjectCredential();
    runSignOutCleanups();
    clearTabSessionStorage();
    if (projectTab) window.location.reload();
}

/** Document bootstrap: follows sign-outs made in this browser's other tabs. */
export function followSignOutElsewhere(): () => void {
    return listenForSignOut(signedOutElsewhere);
}
